// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button } from '@livechat/design-system'
import PlusIcon from 'react-material-icon-svg/dist/PlusIcon'
import firebase from '../firebase'
import SlackApiClient from '../api/slack'
import IntegrationApiClient from '../api/integration'
import AddNotification from './AddNotification'
import NotificationTime from './NotificationTime'
import DeleteNotificationModal from './DeleteNotificationModal'
import SlackLoader from './SlackLoader'
import livechatLogo from '../logo_lc.png'
import slackLogo from '../slack-logo-full.png'

const wrapperCss = css`
  display: flex;
  flex-direction: column;
`

const statusCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-top: 1px solid rgb(221, 226, 230);
  border-right: 1px solid rgb(221, 226, 230);
  border-left: 1px solid rgb(221, 226, 230);
  border-bottom: none;
  border-radius: 4px 4px 0px 0px;
`

const statusTextCss = css`
  font-size: 15px;
  line-height: 1.3;
  color: rgb(122, 130, 137);
  margin: 0;
  padding: 0;
`

const rowHeaderCss = css`
  display: flex;
  background-color: rgb(239, 243, 245);
  border-width: 1px 0px 0px;
  border-right-style: initial;
  border-bottom-style: initial;
  border-left-style: initial;
  border-right-color: initial;
  border-bottom-color: initial;
  border-left-color: initial;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: none;
`

const headerCellCss = width => css`
  box-sizing: border-box;
  background-color: rgb(239, 243, 245);
  padding: 8px 16px;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3;
  color: rgb(66, 77, 87);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  width: ${width}%;
`

const rowCss = css`
  border-left: 1px solid rgb(221, 226, 230);
  border-right: 1px solid rgb(221, 226, 230);
  border-bottom: 1px solid rgb(221, 226, 230);
  padding: 2px 0px;
  display: flex;
`

const cellCss = width => css`
  box-sizing: border-box;
  background-color: rgb(255, 255, 255);
  border: 0px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: rgb(66, 77, 87);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  ${width && `width: ${width}%;`};
`

const actionCellCss = css`
  ${cellCss()};
  flex-grow: 1;
  flex-direction: row;
  justify-content: center;
  position: relative;
`

const dividerCss = css`
  height: 1px;
  background-color: rgb(221, 226, 230);
  width: 100%;
`

const emptyStateCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 20px;
`

const loaderWrapperCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  width: 100%;
  padding-bottom: 20px;
`

const Routing = ({ auth, groups, onNotificationsLoad }) => {
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [formattedNotifications, setFormattedNotifications] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const slackClient = new SlackApiClient()
  const integrationApi = new IntegrationApiClient()

  const getGroup = id => {
    return groups.filter(group => `${group.id}` === `${id}`)[0]
  }

  const fetchNotifications = async () => {
    let firebaseApp

    switch (auth.appName) {
      case 'slack3':
        firebaseApp = firebase.slack3
        break

      case 'slack2':
        firebaseApp = firebase.slack2
        break

      default:
        firebaseApp = firebase.slack
    }

    const db = firebaseApp.firestore()
    const licensesRef = db
      .collection('licenses')
      .doc(`${auth.agentData.license}`)
    const doc = await licensesRef.get()

    const notifications = doc.exists ? doc.data().notifications : []
    notifications.sort((a, b) => {
      return new Date(b.added.on) - new Date(a.added.on)
    })

    return notifications
  }

  const fetchData = async () => {
    setIsFetching(true)

    try {
      const fetchedNotifications = await fetchNotifications()
      await formatChannels(fetchedNotifications)
    } catch (err) {
      console.error(err)
    }

    setIsFetching(false)
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = async () => {
    fetchData()

    await integrationApi.refreshSlack(auth.firebaseToken, auth.appName)
  }

  const handleModalOpen = (notification = null) => {
    setSelectedNotification(notification)
    setIsDeleteModalOpened(true)
  }

  const formatChannels = async notifications => {
    const channelPromises = notifications.map(async notification => {
      const channel = await slackClient.getChannel(
        auth.slack.bot_access_token,
        notification.channel_id
      )

      return channel
    })

    const channels = await Promise.all(channelPromises)
    const filteredNotifications = notifications
      .map((notification, index) => {
        const group = getGroup(notification.group_id)

        return {
          id: notification.id,
          group: group ? group.name : null,
          groupId: notification.group_id,
          channel: channels[index].name,
          channelId: channels[index].id,
          author: notification.added.by,
          time: notification.added.on,
        }
      })
      .filter(notification => !!notification.group)

    setFormattedNotifications(filteredNotifications)
    onNotificationsLoad(filteredNotifications.length)
  }

  const handleDeleteNotification = () => {
    setIsDeleteModalOpened(false)
  }

  return (
    <div
      css={css`
        .lc-form-group {
          margin-bottom: 10px;
        }
      `}
    >
      {isDeleteModalOpened && selectedNotification && (
        <DeleteNotificationModal
          notification={selectedNotification}
          onClose={handleDeleteNotification}
          onDelete={handleChange}
        />
      )}
      <div>
        <AddNotification
          groups={groups}
          onChange={handleChange}
          notifications={formattedNotifications}
        />
      </div>

      {formattedNotifications.length === 0 && !isFetching && (
        <div css={emptyStateCss}>
          <h2>No notifications yet</h2>

          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              margin: 20px 0;
            `}
          >
            <img
              src={slackLogo}
              css={css`
                height: 36px;
              `}
              alt="Slack logo"
            />

            <PlusIcon
              width={36}
              height={36}
              css={css`
                margin: 0 12px 0 10px;
              `}
            />

            <img
              src={livechatLogo}
              css={css`
                height: 45px;
              `}
              alt="LC logo"
            />
          </div>

          <p>
            Create your first notification and start receiving messages directly
            to your Slack workspace.
          </p>

          <p>
            See{' '}
            <a
              href="https://www.sikoraworks.com/apps/slack-for-livechat"
              target="_blank"
              rel="noopener noreferrer"
            >
              tutorial
            </a>{' '}
            for more info.
          </p>
        </div>
      )}

      {isFetching && (
        <div css={loaderWrapperCss}>
          <SlackLoader />
        </div>
      )}

      {formattedNotifications.length > 0 && !isFetching && (
        <div css={wrapperCss}>
          <div css={statusCss}>
            <p css={statusTextCss}>
              Showing {formattedNotifications.length}{' '}
              {formattedNotifications.length > 1 ||
              formattedNotifications.length === 0
                ? 'notifications'
                : 'notification'}
            </p>
          </div>

          <div css={rowHeaderCss}>
            <div css={headerCellCss(30)}>Public Channel</div>
            <div css={headerCellCss(20)}>Group</div>
            <div css={headerCellCss(30)}>Added By</div>
            <div css={headerCellCss(20)}>Actions</div>
          </div>

          <div css={dividerCss}></div>

          {formattedNotifications.map((notification, index) => {
            return (
              <div key={`notification-${index}`}>
                <div css={rowCss}>
                  <div css={cellCss(30)} title={`#${notification.channel}`}>
                    <span
                      css={css`
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        overflow: hidden;
                        width: 100%;
                        line-height: 1.2;
                      `}
                    >
                      #{notification.channel}
                    </span>
                  </div>

                  <div css={cellCss(20)} title={notification.group}>
                    <span
                      css={css`
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        overflow: hidden;
                        width: 100%;
                        line-height: 1.2;
                      `}
                    >
                      {notification.group}
                    </span>
                  </div>

                  <div css={cellCss(30)}>
                    <strong
                      title={notification.author}
                      css={css`
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        overflow: hidden;
                        width: 100%;
                        line-height: 1.2;
                      `}
                    >
                      {notification.author}
                    </strong>

                    <NotificationTime time={notification.time} />
                  </div>
                  <div css={actionCellCss}>
                    <a
                      href={`https://direct.lc.chat/${auth.agentData.license}/${notification.groupId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        secondary
                        size="compact"
                        css={css`
                          margin-right: 10px;
                        `}
                      >
                        Test
                      </Button>
                    </a>

                    <Button
                      destructive
                      size="compact"
                      onClick={() => {
                        handleModalOpen(notification)
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                <div css={dividerCss}></div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Routing
